import { asyncScheduler, EMPTY, from, scheduled } from 'rxjs';
import { filter, map, mergeMap, reduce } from 'rxjs/operators';

import { logger } from '@assess/shared/logger';
import { toArray } from '@assess/shared/type-utilities';

import { validDotPath } from '../../shared/constants/valid-dot-path.constant';
import { IProcessedValidationConfig } from '../types/processed-validation-config.interface';
import { IValidationConfig } from '../types/validation-config.interface';
import { validators } from '../validators/validators';

const preprocessLogger = logger.child({ context: 'preprocessValidationConfigs' });

const preprocess = (config: IValidationConfig) => {
  if (!config?.targetPath || !validDotPath.test(config.targetPath)) {
    preprocessLogger.warn({ data: { config } }, 'Bad target path');

    return EMPTY;
  }

  if (!Array.isArray(config?.validators) || !config?.validators?.length) {
    preprocessLogger.warn({ data: { config } }, 'No validators');

    return EMPTY;
  }

  return scheduled(from(config.validators), asyncScheduler).pipe(
    reduce(
      (acc, v) => {
        if (!validators.has(v.type)) {
          preprocessLogger.warn({ data: { validator: v } }, 'Unknown validator');
        } else if (acc.has(v.type)) {
          preprocessLogger.warn({ data: { validator: v } }, 'Duplicate validator');
        } else {
          acc.set(v.type, { args: v.args, expression: v.expression, message: v.message?.trim() || undefined });
        }

        return acc;
      },
      new Map() as IProcessedValidationConfig['validators']
    ),
    map((validatorMap) => ({ targetPath: config.targetPath, validators: validatorMap }) as IProcessedValidationConfig)
  );
};

export const preprocessValidationConfigs = (configs: IValidationConfig[]) =>
  scheduled(from(toArray(configs)), asyncScheduler).pipe(
    filter((config) => !!config),
    mergeMap((config) => preprocess(config)),
    filter((processedConfig) => !!processedConfig),
    filter((processedConfig) => !!processedConfig.validators.size),
    reduce((acc, processedConfig) => {
      if (acc.has(processedConfig.targetPath)) {
        preprocessLogger.warn({ data: { processedConfig } }, 'Duplicate validation configuration(s)');

        return acc;
      }

      acc.set(processedConfig.targetPath, processedConfig);

      return acc;
    }, new Map<string, IProcessedValidationConfig>())
  );
